import React, { useEffect, useState } from 'react'
import { t } from '../../i18n/i18n'
import { SummaryRow } from '../common/list/SummaryRow'
import { List } from '../common/list/List'
import { getCostIdentifyingColumns, getCostSummaryFields } from '../../helpers/riskEconomyHelper'
import { Button, makeStyles, Text, tokens } from '@fluentui/react-components'
import { ActionPlanStatus, Cost, FinancialYear, FinancialYearStatus } from '../../api/schemas/schema'
import { formatDate, formatToSwedishCrowns } from '../../helpers/stringHelper'
import { BadgeCostCategory } from './BadgeCostCategory'
import { useSearchParams } from 'react-router-dom'
import { CostFilters } from './CostFilters'
import { InfoMessageBar } from '../common/other/InfoMessageBar'
import { AddCircle24Regular } from '@fluentui/react-icons'
import { ModalCost } from './ModalCost'
import { IItemState } from '../../interfaces/IItemState'
import { filterCosts } from '../../helpers/financialYearHelper'
import { CardHeader } from '../common/header/CardHeader'

const useStyles = makeStyles({
   phoneItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalS,
   },
   phoneMoneyOutputWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '50%',
   },
   spaceBtw: {
      display: 'flex',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
   },
   hideInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   hideInDesktop: {
      display: 'none',
      '@media screen and (max-width: 1007px)': {
         display: 'flex',
      },
   },
   addBtn: {
      '@media screen and (max-width: 1007px)': {
         width: '100%',
      },
   },
})

type CostIdentifyingProps = {
   setFinancialYearState: React.Dispatch<React.SetStateAction<IItemState<FinancialYear>>>
   financialYearState: IItemState<FinancialYear>
}
export const CostIdentifying = ({ setFinancialYearState, financialYearState }: CostIdentifyingProps) => {
   const classes = useStyles()
   const financialYear = financialYearState.item
   const { costs } = financialYear
   const [filteredItems, setFilteredItems] = useState<Cost[]>(costs)
   const [searchParams] = useSearchParams()
   const selectedCategory = searchParams.get('category')
   const selectedCostType = searchParams.get('type')
   const [modalOpen, setModalOpen] = useState<boolean>(false)
   const [selectedCost, setSelectedCost] = useState<Cost>(null)

   const disabled = financialYearState.disabled || financialYear.status === FinancialYearStatus.Completed

   useEffect(() => {
      setFilteredItems(filterCosts(costs, selectedCategory, selectedCostType))
   }, [costs, selectedCategory, selectedCostType])
   const onRowClick = (item: Cost) => {
      setSelectedCost(item)
      setModalOpen(true)
   }
   return (
      <>
         <CardHeader
            title={t('IdentifyCosts')}
            action={
               <Button className={classes.addBtn} appearance="primary" icon={<AddCircle24Regular />} onClick={() => setModalOpen(true)} disabled={disabled}>
                  {t('AddCost')}
               </Button>
            }
         >
            <CostFilters className={classes.hideInPhone} financialYear={financialYear} />
         </CardHeader>
         <CostFilters className={classes.hideInDesktop} financialYear={financialYear} />
         <>
            {costs.length === 0 ? (
               <InfoMessageBar title={t('TimeToAddYourFirstCost')} />
            ) : (
               <>
                  <SummaryRow summaryFields={getCostSummaryFields(filteredItems)} />
                  <List
                     columns={getCostIdentifyingColumns()}
                     items={filteredItems}
                     columnSizingOptions={{
                        date: { minWidth: 80, idealWidth: 150 },
                        category: { minWidth: 150, idealWidth: 200 },
                        post: { minWidth: 150, idealWidth: 300 },
                        description: { minWidth: 400, idealWidth: 1100 },
                        budget: { minWidth: 200, idealWidth: 250 },
                        result: { minWidth: 200, idealWidth: 250 },
                     }}
                     resizableColumns
                     onRowClick={onRowClick}
                     renderPhoneBodyItem={(item, index) => {
                        return (
                           <div className={classes.phoneItem} key={item.id}>
                              <span className={classes.spaceBtw}>
                                 <Text size={400} weight="bold">
                                    {formatDate(item.created)}
                                 </Text>
                                 <BadgeCostCategory category={item.post.costCategory} />
                              </span>
                              <Text size={400}>{item.post.name ?? '-'}</Text>
                              <span className={classes.phoneMoneyOutputWrapper}>
                                 <Text size={400} weight="bold">{`${t('Budget')} `}</Text>
                                 <Text size={400}>{formatToSwedishCrowns(item.budget ?? 0) + ' kr'}</Text>
                              </span>
                              <span className={classes.phoneMoneyOutputWrapper}>
                                 <Text size={400} weight="bold">{`${t('Result')} `}</Text>
                                 <Text size={400}>{formatToSwedishCrowns(item.result ?? 0) + ' kr'}</Text>
                              </span>
                           </div>
                        )
                     }}
                  />
               </>
            )}
         </>
         <ModalCost
            setFinancialYearState={setFinancialYearState}
            financialYearState={financialYearState}
            close={() => {
               setSelectedCost(null)
               setModalOpen(false)
            }}
            selectedCost={selectedCost}
            open={modalOpen}
            disabled={
               financialYearState.disabled ||
               financialYear.status === FinancialYearStatus.Completed ||
               (selectedCost && financialYear.actionPlans.some((ap) => ap.costId === selectedCost.id && ap.status !== ActionPlanStatus.NotDecided))
            }
         />
      </>
   )
}
